import React from 'react';
import { cn } from '@/utils/cn';

interface RefreshButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Show spinning icon and "Refreshing..." text */
  isRefreshing?: boolean;
  /** Button label when not refreshing */
  label?: string;
  /** Size variant */
  size?: 'sm' | 'md';
}

/**
 * Reusable refresh button with a circular arrow icon.
 * Displays a spinning icon and "Refreshing..." text while loading.
 */
export const RefreshButton: React.FC<RefreshButtonProps> = ({
  isRefreshing = false,
  label = 'Refresh',
  size = 'md',
  disabled,
  className,
  ...props
}) => {
  const sizes = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
  };

  return (
    <button
      type="button"
      disabled={disabled || isRefreshing}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md font-medium transition-colors whitespace-nowrap',
        sizes[size],
        isRefreshing
          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
          : 'text-gray-600 hover:bg-gray-100 border border-gray-300',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      {...props}
    >
      <span className={cn('inline-block', isRefreshing && 'animate-spin')}>↻</span>
      {isRefreshing ? 'Refreshing...' : label}
    </button>
  );
};
